import { fetchFeatureFlag } from '../api/featureFlag';
import { type FeatureFlagName, featureFlags } from './featureFlags';

// Cache em memória (por sessão) dos valores vindos da API, compartilhado entre componentes.
const values = new Map<FeatureFlagName, boolean>();
const pending = new Map<FeatureFlagName, Promise<void>>();
const listeners = new Set<() => void>();

const notify = () => {
    for (const listener of listeners) {
        listener();
    }
};

export const subscribeFeatureFlags = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const getFeatureFlagValue = (flag: FeatureFlagName): boolean => values.get(flag) ?? featureFlags[flag];

/**
 * Busca a flag na API uma única vez (requisições simultâneas são deduplicadas).
 * - 200: usa `enabled`
 * - 404: flag inexistente, mantém o default local
 * - erro: mantém o default local e não faz cache, para tentar de novo no próximo mount
 */
export const loadFeatureFlag = (flag: FeatureFlagName): Promise<void> => {
    if (values.has(flag)) {
        return Promise.resolve();
    }

    const inFlight = pending.get(flag);
    if (inFlight) {
        return inFlight;
    }

    const request = fetchFeatureFlag(flag)
        .then(response => {
            values.set(flag, response ? response.enabled : featureFlags[flag]);
            notify();
        })
        .catch(error => {
            console.error(`Error fetching feature flag "${flag}":`, error);
        })
        .finally(() => {
            pending.delete(flag);
        });

    pending.set(flag, request);
    return request;
};

export const resetFeatureFlagStore = () => {
    values.clear();
    pending.clear();
    notify();
};
